import React from 'react';
import PropTypes from 'prop-types';
import DialogTemplate from '../../dialogs_common/DialogTemplate';
import {OutlineButton, SolidButton} from '../../components/Button';
import FormTemplate from '../../dialogs_common/FormTemplate';
import {useUser} from '@lore/auth';
import FormTemplateButtonFooter from '../../dialogs_common/FormTemplateButtonFooter';
import useIntents from "../../hooks/useIntents";
import Loader from "../../components/Loader";

InviteRedirectDialog.propTypes = {
  user: PropTypes.object.isRequired
};

const messageType = {
  "0000": "Both/Both",
  "0001": "Educator/Partner", // "Both/Partner" but treat the other as an educator
  "0010": "Partner/Educator", // "Both/Educator" but treat the other as a partner

  "0100": "Partner/Educator", // "Partner/Both" but treat the current as an educator
  "0101": "Partner/Partner",
  "0110": "Partner/Educator",

  "1000": "Educator/Partner", // "Educator/Both" but treat the current as a partner
  "1001": "Educator/Partner",
  "1010": "Educator/Educator",

  "0011": "NOOP",
  "0111": "NOOP",
  "1011": "NOOP",
  "1100": "NOOP",
  "1101": "NOOP",
  "1110": "NOOP",
  "1111": "NOOP",
};

/**
 * Get the kind of redirect message to show to the user.
 * @param {boolean} A Other (user to whom we are trying to connect) is ONLY an educator.
 * @param {boolean} B Other (user to whom we are trying to connect) is ONLY a partner.
 * @param {boolean} X Current user is ONLY an educator.
 * @param {boolean} Y Current user is ONLY a partner.
 * @returns string
 */
function getMessageType(A, B, X, Y) {
  // make sure we have booleans
  A = !!A;
  B = !!B;
  X = !!X;
  Y = !!Y;

  const key = `${+A}${+B}${+X}${+Y}`; // convert booleans to 0/1
  return messageType[key] || "NOOP";
}

const messages = {
  "Partner/Partner": {
    title: "You can't connect with partners at this time.",
    subtitle: "Currently only educators can connect with partners. We are exploring how we might support Partner-to-Partner connections, but we do not offer a way to do that at this time."
  },
  "Educator/Educator": {
    title: "You can't connect with educators at this time.",
    subtitle: "Currently only partners can connect with educators. We are exploring how we might support Educator-to-Educator connections, but we do not offer a way to do that at this time.",
    suggestion: "Consider reaching out to this educator via email for now."
  },
  "Partner/Educator": {
    title: "Looks like you want to connect with a partner.",
    subtitle: "That's great! The first step is to create a project that describes what you're looking for, and then you'll be able to invite partners to collaborate.",
    suggestion: "Would you like to create a project now?",
    button: "Yes, I want to create a Project",
    submit: "project"
  },
  "Educator/Partner": {
    title: "Looks like you want to connect with an educator.",
    subtitle: "That's great! The first step is to create an offer that describes what you're looking for, and then you'll be able to invite educators to participate.",
    suggestion: "Would you like to create an offer now?",
    button: "Yes, I want to create an Offer",
    submit: "offer"
  },
  "Both/Both": {
    title: "Looks like you want to connect with a partner or educator.",
    subtitle: "That's great! The first step is to create an offer or project that describes what you're looking for, and then you'll be able to invite educators or partners to participate.",
    suggestion: "Would you like to create a project or an offer now?",
    button: "Create a Project",
    secondary: "Create an Offer",
    submit: "project",
    submitSecondary: "offer"
  },
  // this should never be shown but for completeness or error cases
  "NOOP": {
    title: "You can't connect at this time.",
    subtitle: "We are exploring how we might support other connections, but we do not offer a way to do that at this time."
  }
}

export default function InviteRedirectDialog(props) {
  const {user} = props;

  const currentUser = useUser();

  const {
    isOnlyPartner: otherUserIsOnlyPartner,
    isOnlyEducator: otherUserIsOnlyEducator,
    fetching: fetchingOther
  } = useIntents(user);
  const {
    isOnlyEducator: currentUserIsOnlyEducator,
    isOnlyPartner: currentUserIsOnlyPartner,
    fetching: fetchingCurrent
  } = useIntents(currentUser);

  /**
   * Start and offer or project flow
   * @param {string|undefined} kind
   */
  function onSubmit(kind) {
    props.onCancel();
    props.onSubmit && props.onSubmit(kind);
  }

  if (fetchingOther || fetchingCurrent) {
    return (
      <DialogTemplate size="medium">
        <div className="p-6 w-96 h-64">
          <Loader/>
        </div>
      </DialogTemplate>
    );
  }

  const messageType = getMessageType(
    otherUserIsOnlyEducator, otherUserIsOnlyPartner,
    currentUserIsOnlyEducator, currentUserIsOnlyPartner);
  const message = messages[messageType];

  return (
    <DialogTemplate size="medium">
      <FormTemplate
        title={message.title}
        onClose={props.onCancel}
        body={(
          <>
            <p>{message.subtitle}</p>
            {message.suggestion && <p>{message.suggestion}</p>}
            {/*{otherUserIsOnlyEducator && <small>target is educator</small>}*/}
            {/*{otherUserIsOnlyPartner && <small>target is partner</small>}*/}
            {/*{!otherUserIsOnlyEducator && !otherUserIsOnlyPartner && <small>target is both</small>}*/}
            {/*{<span>:</span>}*/}
            {/*{currentUserIsOnlyEducator && <small>current is educator</small>}*/}
            {/*{currentUserIsOnlyPartner && <small>current is partner</small>}*/}
            {/*{!currentUserIsOnlyEducator && !currentUserIsOnlyPartner && <small>current is both</small>}*/}
          </>
        )}
        footer={(
          <FormTemplateButtonFooter>
            <OutlineButton className="mr-4" onClick={props.onCancel}>
              Cancel
            </OutlineButton>
            {message.button &&
              <SolidButton onClick={() => onSubmit(message.submit)}>
                {message.button}
              </SolidButton>
            }
            {message.secondary &&
              <button className="btn btn-tertiary"
                      onClick={() => onSubmit(message.submitSecondary)}>
                {message.secondary}</button>
            }
          </FormTemplateButtonFooter>
        )}
      />
    </DialogTemplate>
  );
};
